import copy from "copy-to-clipboard";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FacebookIcon, LinkedinIcon, TwitterIcon } from "react-share";

import {
  Container,
  Dialog,
  Grid,
  Icon,
  Mapbox,
  Popover,
  Pressable,
  Row,
} from "../components";
import { Amenity, ImageData, Location, Space } from "../data";
import { PartnerOffering } from "../data/partner_offering";
import { useMediaQuery, useQueryString } from "../hooks";
import { useI18n } from "../lib/i18n/i18n_provider";
import { BackButton } from "./back_button";
import { Button } from "./button";
import { Carousel } from "./carousel";
import { ContactFormProvider } from "./contact_form_provider";
import { Content } from "./content";
import { Divider } from "./divider";
import { Footer } from "./footer";
import { Heading } from "./heading";
import { Hidden } from "./hidden";
import { ImageGalleryDesktop } from "./image_gallery_desktop";
import { ImageGalleryMobile } from "./image_gallery_mobile";
import { OfferingHeader } from "./offering_header";
import { PageLayout } from "./page_layout";
import { Spacer } from "./spacer";
import { Text } from "./text";
import { TextLink } from "./text_link";
import { Visible } from "./visible";
import { OfferingFooterMobile } from "./offering_footer_mobile";
import {
  OfferingMessageType,
  useTestimonialsQuery,
  useSpaceAmenitiesQuery,
} from "./api";
import PromoCodeBox from "../core/promo_code_box";
import { PartnerConfig } from "./partner_config";
import { TemplateTestimonial } from "./template_testimonial";
import { Testimonial } from "../data/partners";
import { OfferingRibbon } from "./offering_ribbon";
import { useAnalytics } from "../lib/analytics/analytics";

interface OfferingDetailsBaseProps {
  partnerOfferingID: string;
  partnerOffering: PartnerOffering;
  checkoutBoxDesktop: React.ReactNode;
  checkoutBoxMobile: React.ReactNode;
  showTalkWithUsPrompt?: boolean;
  showAddress: boolean;
  showDirections: boolean;
  onPressBack?: () => void;
  space: Space | undefined;

  partnerConfig: PartnerConfig;

  data: {
    title?: string | React.ReactNode;
    pageTitle?: string;
    /** @deprecated Use `title` and `pageTitle` */
    offeringName: string;
    capacity?: number;
    location?: Location;
    description?: React.ReactNode;
    amenitiesDescription?: string;
    faqs?: FAQ[];
    testimonials?: Testimonial[];
    imagesMobile?: ImageData[];
    imagesDesktop?: ImageData[];
    imagesModal?: ImageData[];
    generalInfo?: GeneralInformation;
  };
}

export function OfferingDetailsBase(props: OfferingDetailsBaseProps) {
  const {
    checkoutBoxDesktop,
    checkoutBoxMobile,
    partnerOfferingID,
    partnerConfig,
    onPressBack,
    data,
    space,
    partnerOffering,
    showTalkWithUsPrompt,
    showAddress,
    showDirections,
  } = props;
  const {
    imageForIllustrationOnly,
    favicon,
    hubspotForm,
    showLanguageSelector = false,
  } = partnerConfig;

  const {
    title,
    pageTitle,
    offeringName,
    capacity,
    description,
    amenitiesDescription,
    location,
    imagesMobile,
    imagesDesktop,
    imagesModal,
  } = data;
  const { locale } = useI18n();

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0 });
    }, 0);
  }, []);

  const selectedOfferingLocation = partnerOffering.offeringLocations.find(
    (p) => p.spaceID === space?.id
  );
  const featuredPromoDescription =
    selectedOfferingLocation?.featuredPromoDescription;
  const generalInfo =
    selectedOfferingLocation?.generalInfo || partnerOffering.generalInfo;
  const faqs = selectedOfferingLocation?.faqs || partnerOffering.faqs;

  const { data: amenities } = useSpaceAmenitiesQuery(space?.id);
  const { data: partnerOfferingTestimonials } = useTestimonialsQuery(
    partnerOffering.id
  );
  const testimonials =
    partnerOfferingTestimonials.length > 0
      ? partnerOfferingTestimonials
      : data.testimonials;

  return (
    <ContactFormProvider
      partnerID={partnerOffering.partner.id}
      messageableID={partnerOfferingID}
      messageableType={OfferingMessageType.PartnerOffering}
      hubspotForm={hubspotForm}
    >
      <Container>
        <Helmet>
          <title>
            {pageTitle ||
              (typeof title === "string"
                ? title
                : `${offeringName} - ${partnerOffering.partner.name}`)}{" "}
          </title>
          <link href={favicon} rel="shortcut icon" type="image/x-icon" />
        </Helmet>
        <OfferingHeader
          offeringID={partnerOfferingID}
          homepageURL={partnerOffering.partner.homepageUrl}
          logo={partnerOffering.partner.logoUrl}
          hubspotForm={hubspotForm}
          showLanguageSelector={showLanguageSelector}
          localeOptions={partnerConfig.localeOptions}
          showTalkWithUsPrompt={showTalkWithUsPrompt}
          partnerID={partnerOffering.partner.id}
        />
        <Content>
          {onPressBack && (
            <Container paddingHorizontal={16}>
              <Spacer size={16} />
              <BackButton onPress={onPressBack} />
              <Spacer size={16} />
            </Container>
          )}
        </Content>
        <Hidden sm>
          <Container zIndex={2}>
            {imagesMobile && (
              <ImageGalleryMobile
                images={imagesMobile}
                name={offeringName}
                featuredPromoRibbon={
                  featuredPromoDescription ? (
                    <OfferingRibbon ribbonMessage={featuredPromoDescription} />
                  ) : undefined
                }
              />
            )}
            {imageForIllustrationOnly && <ImageForIllustrationOnly />}
            <div style={{ position: "absolute", top: 16, right: 16 }}>
              <SocialMediaShare shareIconColor="#FFFFFF" />
            </div>
          </Container>
        </Hidden>
        <Visible sm>
          <Content>
            <Container>
              {imagesDesktop && (
                <ImageGalleryDesktop
                  images={imagesDesktop}
                  name={offeringName}
                  imagesModal={imagesModal}
                  featuredPromoRibbon={
                    featuredPromoDescription ? (
                      <OfferingRibbon
                        ribbonMessage={featuredPromoDescription}
                      />
                    ) : undefined
                  }
                />
              )}
              {imageForIllustrationOnly && (
                <div style={{ marginLeft: 16, position: "relative" }}>
                  <ImageForIllustrationOnly />
                </div>
              )}
            </Container>
          </Content>
        </Visible>
        <Spacer size={24} />
        <PageLayout
          content={
            <Container>
              <OfferingIntro
                title={title || offeringName}
                description={
                  <Text>
                    {space?.description || partnerOffering.description ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            space?.description || partnerOffering.description,
                        }}
                      />
                    ) : (
                      description
                    )}
                  </Text>
                }
                promo={
                  <PromoCodeBox
                    spaceID={space?.id}
                    locale={locale}
                    partnerOffering={partnerOffering}
                  />
                }
                capacity={capacity}
                space={space}
                address={
                  showAddress && location
                    ? `${location.streetAddress}, ${location.city}`
                    : ""
                }
              />
              {amenities && amenities.length > 0 && (
                <Fragment>
                  <SectionDivider />
                  <OfferingAmenities
                    amenitiesDescription={amenitiesDescription}
                    amenities={amenities}
                  />
                </Fragment>
              )}
              <SectionDivider />
              {!!generalInfo ? (
                <div
                  className="general-info"
                  dangerouslySetInnerHTML={{ __html: generalInfo }}
                />
              ) : (
                data.generalInfo &&
                data.generalInfo?.bulletPoints.length > 0 && (
                  <OfferingGeneralInformation generalInfo={data.generalInfo} />
                )
              )}
              {faqs ? (
                <OfferingFAQ>
                  <FAQsHtmlContent faqs={faqs} />
                </OfferingFAQ>
              ) : data.faqs ? (
                <OfferingFAQ>
                  <FAQsReactContent faqs={data.faqs} />
                </OfferingFAQ>
              ) : null}
            </Container>
          }
          rightSidebar={checkoutBoxDesktop}
        />
        {showDirections && location && (
          <Fragment>
            <Spacer size={32} />
            <LocationMap location={location} />
          </Fragment>
        )}
        {testimonials && <OfferingTestimonials testimonials={testimonials} />}
        <Spacer size={24} />
        <OfferingFooterMobile
          showLanguageSelector={showLanguageSelector}
          hubspotForm={hubspotForm}
          partnerID={partnerOffering.partner.id}
          offeringID={partnerOfferingID}
          showTalkWithUsPrompt={showTalkWithUsPrompt}
          localeOptions={partnerConfig.localeOptions}
        />
        <Hidden md>
          {checkoutBoxMobile}
          <Spacer size={40} />
        </Hidden>
        <Visible md>
          <Footer displayAccessibility={partnerConfig.displayAccessibility} />
        </Visible>
      </Container>
    </ContactFormProvider>
  );
}

export function ImageForIllustrationOnly() {
  const { fbt, locale } = useI18n();
  return (
    <div
      style={{
        background: "rgba(239, 239, 255, 0.7)",
        padding: 4,
        position: "absolute",
        bottom: 8,
        zIndex: 3,
        ...(locale.direction === "rtl" ? { right: 24 } : { left: 8 }),
      }}
    >
      <Text>{fbt("Image for illustration purpose only")}</Text>
    </div>
  );
}

export function SectionDivider() {
  return (
    <Container paddingHorizontal={16}>
      <Spacer size={8} />
      <Divider />
      <Spacer size={8} />
    </Container>
  );
}

interface OfferingAmenitiesProps {
  amenities: Amenity[];
  amenitiesDescription?: string;
}

export function OfferingAmenities(props: OfferingAmenitiesProps) {
  const { amenities, amenitiesDescription } = props;
  const { fbt } = useI18n();

  return (
    <div className="template-offering-details-base-amenities">
      <Container paddingHorizontal={16}>
        <Spacer size={24} />
        <Heading>{fbt("Amenities")}</Heading>
        <Spacer size={16} />
        <Grid itemsPerRow={2} gap={24}>
          {amenities.map((amenity) => (
            <Container key={amenity.id}>
              <Row alignItems="center">
                <img
                  alt={`${amenity.name} icon`}
                  src={amenity.iconURL}
                  style={{ width: 28, height: 28 }}
                />
                <Spacer size={12} direction="horizontal" />
                <Text>{amenity.name}</Text>
              </Row>
            </Container>
          ))}
        </Grid>
        {amenitiesDescription && (
          <Fragment>
            <Spacer size={16} />
            <Text>{amenitiesDescription}</Text>
          </Fragment>
        )}
      </Container>
    </div>
  );
}

export interface GeneralInformation {
  title: string;
  description?: string;
  bulletPoints: { point?: string; text: React.ReactNode }[];
}
interface OfferingGeneralInformationProps {
  generalInfo: GeneralInformation;
}

export function OfferingGeneralInformation(
  props: OfferingGeneralInformationProps
) {
  const { generalInfo } = props;
  const { title, description, bulletPoints } = generalInfo;

  return (
    <Container paddingHorizontal={16}>
      <Spacer size={24} />
      <Heading>{title}</Heading>
      <Spacer size={16} />
      {description && (
        <Fragment>
          <Text>{description}</Text>
          <Spacer size={32} />
        </Fragment>
      )}
      {bulletPoints.map((p, i) => (
        <Container key={p.point || `${i}`}>
          <Row>
            <Icon size={20} name="check-circle" />
            <Spacer size={16} direction="horizontal" />
            <div
              className="template-offering-details-base-general-info-bullet-point"
              style={{ flex: 1 }}
            >
              <Text>
                {p.point && <Text weight="bold">{p.point} - </Text>} {p.text}
              </Text>
            </div>
          </Row>
          <Spacer size={24} />
        </Container>
      ))}
    </Container>
  );
}

interface OfferingTestimonialsProps {
  testimonials: Testimonial[];
}

export function OfferingTestimonials(props: OfferingTestimonialsProps) {
  const { testimonials } = props;
  const { fbt } = useI18n();

  return (
    <div
      className="template-offering-details-base-offering-testimonials"
      style={{
        boxSizing: "border-box",
        paddingLeft: 16,
        paddingRight: 16,
      }}
    >
      <Spacer size={48} />
      <Heading
        className="template-offering-details-base-heading-testimonial"
        size="xl"
        align="center"
      >
        {fbt("Our Customers Say")}
      </Heading>
      <Spacer size={24} />
      <Hidden md>
        <Carousel slidesCount={1}>
          {testimonials.map((testimonial) => {
            return (
              <Fragment key={testimonial.name}>
                {TemplateTestimonial(testimonial)}
              </Fragment>
            );
          })}
        </Carousel>
      </Hidden>
      <Visible md>
        <Content>
          <Carousel slidesCount={testimonials.length === 2 ? 2 : 3}>
            {testimonials.map((testimonial) => {
              return (
                <Fragment key={testimonial.name}>
                  {TemplateTestimonial(testimonial)}
                </Fragment>
              );
            })}
          </Carousel>
        </Content>
      </Visible>
      <Spacer size={48} />
    </div>
  );
}

export interface FAQ {
  question: string;
  answer: React.ReactNode;
}

interface OfferingFAQProps {
  children?: any;
}

export function OfferingFAQ(props: OfferingFAQProps) {
  const { children } = props;
  const qs = useQueryString();
  const [visible, setVisible] = useState(!!qs.faq);
  const { fbt } = useI18n();
  const analytics = useAnalytics();

  const handleOpen = useCallback(() => {
    setVisible(true);
    analytics.event(
      "FAQ Click",
      {
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [analytics]);

  return (
    <Fragment>
      <SectionDivider />
      <Container paddingHorizontal={16}>
        <Spacer size={24} />
        <Row alignItems="center">
          <Text>{fbt("Have more questions?")}</Text>
          <Spacer size={4} direction="horizontal" />
          <TextLink onPress={handleOpen}>{fbt("Visit our FAQ")}</TextLink>
        </Row>
        <Spacer size={24} />
        <Dialog onRequestClose={() => setVisible(false)} isOpen={visible}>
          <Container
            maxWidth={600}
            padding={16}
            className="offering_details__faqs"
          >
            <Pressable onPress={() => setVisible(false)}>
              <Icon name="x" />
            </Pressable>
            <Spacer size={16} />
            <Heading>{fbt("Frequently asked questions")}</Heading>
            <Spacer size={16} />
            {children}
            <Spacer size={16} />
            <Spacer />
            <Button onPress={() => setVisible(false)} title={fbt("Close")} />
          </Container>
        </Dialog>
      </Container>
    </Fragment>
  );
}

interface FAQsHtmlContentProps {
  faqs: string;
}
function FAQsHtmlContent(props: FAQsHtmlContentProps) {
  const { faqs } = props;
  return (
    <Fragment>
      <div className="faqs" dangerouslySetInnerHTML={{ __html: faqs }} />
    </Fragment>
  );
}
interface FAQsReactContentProps {
  faqs: FAQ[];
}
function FAQsReactContent(props: FAQsReactContentProps) {
  const { faqs } = props;
  return (
    <Fragment>
      {faqs &&
        faqs.map((faq) => (
          <Container key={faq.question}>
            <Text weight="bold">{faq.question}</Text>
            <Text>{faq.answer}</Text>
            <Spacer size={16} />
          </Container>
        ))}
    </Fragment>
  );
}

interface SocialMediaShareProps {
  shareIconColor?: string;
}

export function SocialMediaShare(props: SocialMediaShareProps) {
  const { shareIconColor } = props;
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${window.location.pathname}`;
  const analytics = useAnalytics();

  const handleCopy = useCallback(() => {
    copy(url);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      setOpen(false);
    }, 640);
  }, [url]);

  const handleShareOnFacebook = React.useCallback(() => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    );
  }, [url]);

  const handleShareOnTwitter = React.useCallback(() => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`
    );
  }, [url]);

  const handleShareOnLinkedIn = React.useCallback(() => {
    window.open(
      `https://linkedin.com/shareArticle?url=${encodeURIComponent(url)}`
    );
  }, [url]);

  const onOpen = useCallback(() => {
    const newValue = !open;

    setOpen(newValue);

    if (!newValue) {
      return;
    }

    analytics.event(
      "Share Click",
      {
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [analytics, open]);

  return (
    <Popover<HTMLDivElement>
      autoFocus
      isOpen={open}
      onRequestClose={() => setOpen(false)}
      position="bottom-right"
      content={
        <Container
          zIndex={99}
          width={240}
          backgroundColor="white"
          paddingVertical={16}
          borderWidth={1}
          borderColor="#d7d5dd"
        >
          <Container paddingHorizontal={16}>
            <Text size="lg" weight="bold">
              Share
            </Text>
          </Container>
          <Spacer size={16} />
          {copied ? (
            <Container height={40} paddingHorizontal={16}>
              <Row expanded alignItems="center">
                <Text>Copied!</Text>
              </Row>
            </Container>
          ) : (
            <Pressable
              style={{ backgroundColor: "white" }}
              onPress={handleCopy}
            >
              <Container height={40} paddingHorizontal={16}>
                <Row expanded alignItems="center">
                  <Icon name="copy" size={20} />
                  <Spacer size={8} direction="horizontal" />
                  <Text>Copy</Text>
                </Row>
              </Container>
            </Pressable>
          )}
          <Pressable
            style={{ backgroundColor: "white" }}
            onPress={handleShareOnTwitter}
          >
            <Container height={40} paddingHorizontal={16}>
              <Row expanded alignItems="center">
                <TwitterIcon size={20} />
                <Spacer size={8} direction="horizontal" />
                <Text>Twitter</Text>
              </Row>
            </Container>
          </Pressable>
          <Pressable
            style={{ backgroundColor: "white" }}
            onPress={handleShareOnLinkedIn}
          >
            <Container height={40} paddingHorizontal={16}>
              <Row expanded alignItems="center">
                <LinkedinIcon size={20} />
                <Spacer size={8} direction="horizontal" />
                <Text>LinkedIn</Text>
              </Row>
            </Container>
          </Pressable>
          <Pressable
            style={{ backgroundColor: "white" }}
            onPress={handleShareOnFacebook}
          >
            <Container height={40} paddingHorizontal={16}>
              <Row expanded alignItems="center">
                <FacebookIcon size={20} />
                <Spacer size={8} direction="horizontal" />
                <Text>Facebook</Text>
              </Row>
            </Container>
          </Pressable>
          <Spacer size={16} />
          <Container paddingHorizontal={16}>
            <TextLink onPress={() => setOpen(!open)}>Close</TextLink>
          </Container>
        </Container>
      }
    >
      {({ ref }) => (
        <div ref={ref}>
          <Pressable onPress={onOpen}>
            <Icon name="share" color={shareIconColor} />
          </Pressable>
        </div>
      )}
    </Popover>
  );
}

interface OfferingIntroProps {
  title: string | React.ReactNode;
  description: React.ReactNode;
  capacity?: number;
  address?: string;
  promo?: React.ReactNode;
  space: Space | undefined;
}

export function OfferingIntro(props: OfferingIntroProps) {
  const { title, address, description, capacity, promo, space } = props;
  const mq = useMediaQuery();
  const { fbt } = useI18n();

  return (
    <Container zIndex={1} paddingHorizontal={16}>
      <Row>
        <Container flex={1}>
          <Row>
            <Container flex={1}>
              {typeof title === "string" ? (
                <Heading size="xl">{title}</Heading>
              ) : (
                title
              )}
            </Container>
            <Visible md>
              <Container>
                <SocialMediaShare />
              </Container>
            </Visible>
          </Row>
          <Spacer size={8} />
          <Row alignItems="center" wrap>
            {capacity !== undefined && capacity !== 0 && (
              <Row alignItems="center">
                <Icon name="capacity" />
                <Spacer size={6} direction="horizontal" />
                <Text>
                  {fbt(
                    "Up to " +
                      fbt.param("capacity", capacity) +
                      (capacity > 1 ? " people" : " person")
                  )}
                </Text>
              </Row>
            )}
            {capacity !== undefined && capacity !== 0 && address && (
              <Spacer size={24} direction="horizontal" />
            )}
            {address && (
              <Row alignItems="center">
                <Icon name="location" />
                <Spacer size={6} direction="horizontal" />
                <Text>{address}</Text>
              </Row>
            )}
            {mq.device === "desktop" && (
              <Fragment>
                {space?.attributes.floor && (
                  <Fragment>
                    <Spacer size={24} direction="horizontal" />
                    <Text> ∙ Floor {space.attributes.floor}</Text>
                  </Fragment>
                )}
                <Spacer size={4} direction="horizontal" />
                {space?.attributes.withWindow && <Text> ∙ With window</Text>}
              </Fragment>
            )}
          </Row>
          {(capacity !== undefined ||
            address ||
            space?.attributes.floor ||
            space?.attributes.withWindow) && <Spacer size={16} />}
          {mq.device !== "desktop" && (
            <Row alignItems="center">
              {space?.attributes.floor && (
                <Text> ∙ Floor {space.attributes.floor}</Text>
              )}
              <Spacer size={4} direction="horizontal" />
              {space?.attributes.withWindow && <Text> ∙ With window</Text>}
            </Row>
          )}
          <Spacer size={8} />
          {description}
          {promo}
        </Container>
      </Row>
      <Spacer size={24} />
    </Container>
  );
}

interface LocationMapProps {
  location: Location;
}

export function LocationMap(props: LocationMapProps) {
  const { location } = props;
  const { name, streetAddress, city, direction, latitude, longitude } =
    location;
  const { fbt } = useI18n();

  return (
    <Container>
      <Content>
        <Container paddingHorizontal={16}>
          <Heading>{fbt("Directions")}</Heading>
          <Spacer size={16} />
          <Text>
            {direction ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: direction,
                }}
              />
            ) : (
              `${name} ${fbt("is located at")} ${streetAddress}, ${city}`
            )}
          </Text>
        </Container>
      </Content>
      <Spacer size={16} />
      {/* The mapbox height is 364 */}
      <Container height={364}>
        <Mapbox latitude={latitude} longitude={longitude} />
      </Container>
    </Container>
  );
}
